import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';

import {basePath} from '@atmi/core/injection';
import {CommonCard, ResponsePaginateSuccess, ResponseSuccess, SourceArchivial} from '@atmi/core/models';
import {cleanNullableValue, generateIndexQueryString, generatePlainQueryParamsFromObject, IndexQueryStringType} from '@atmi/core/utils';
import {addSourceArc, deleteSourceArc, setSourceArc, updateSourceArc} from '@src/app/store/source-arc.repository';
import {firstValueFrom} from 'rxjs';

import {first, map, tap} from 'rxjs/operators';

import {KeyGeneratorService} from './key-generator.service';

@Injectable()
export class SourceArcApiService {

  private readonly url = `${this.baseUrl}/documenti-arc`;

  constructor(
    @Inject(basePath) private readonly baseUrl: string,
    private readonly http: HttpClient,
    private readonly keyGeneratorService: KeyGeneratorService,
  ) {
  }


  getAll() {
    return this.http.get<ResponsePaginateSuccess<SourceArchivial[]>>(this.url, {})
      .pipe(
        map((res) => ({data: res.data, count: res.count})),
        tap((res) => {
          setSourceArc(res.data);
          return res;
        }),
        first(),
      );
  }

  index(queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);

    return this.http.get<ResponsePaginateSuccess<SourceArchivial[]>>(this.url, {params}).pipe(
      map((res) => ({data: res.data, count: res.count})),
      first(),
    );
  }

  get(id: string) {
    return this.http.get<ResponseSuccess<SourceArchivial>>(`${this.url}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  post(sourceArc: SourceArchivial) {
    return this.http.post<ResponseSuccess<SourceArchivial>>(this.url, cleanNullableValue(sourceArc)).pipe(
      map((res) => res.data),
      tap((sourceArc) => {
        addSourceArc(sourceArc);
        return sourceArc;
      }),
      first(),
    );
  }

  async getMany(ids: string[]) {
    return await Promise.all(ids.map(async (id) => firstValueFrom(this.get(id).pipe(first()))));
  }


  put(sourceArc: SourceArchivial) {
    return this.http.put<ResponseSuccess<SourceArchivial>>(`${this.url}/${sourceArc.id}`, sourceArc).pipe(
      map((res) => res.data),
      tap((sourceArc) => {
        updateSourceArc(sourceArc.id, sourceArc);
        return sourceArc;
      }),
      first(),
    );
  }

  remove(id: string) {
    return this.http.delete(`${this.url}/${id}`)
      .pipe(tap((sourceArc) => {
        deleteSourceArc(id);
        return sourceArc;
      }), first());
  }

  getCards(id: string, queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);
    const url = `${this.url}/${id}/beni`;

    return this.http.get<ResponsePaginateSuccess<CommonCard[]>>(url, {params}).pipe(
      map((res) => ({data: res.data, count: res.count})),
      first(),
    );
  }

  exportArc(id: string, template: string = 'default', format = 'pdf') {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();

    let url = `${this.url}/${id}/export?template=${template}&format=${format}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, {responseType: 'blob'});
  }

  exportList(
    template: string = 'default',
    queryString: IndexQueryStringType,
    selection?: string[],
  ) {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();
    const params = generatePlainQueryParamsFromObject({...queryString, selection});

    let url = `${this.url}/export?template=${template}&${params}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, {responseType: 'blob'});
  }
}
